import type { DateTime } from 'luxon'
import { onMounted } from 'vue'
import type { PortComplex } from '~/services/apiClient'
import { formatBucketWidth } from '~/models/capacitySets'
import { useCustomerStore } from '~/stores/customers'

export const BUCKET_MINUTE_OPTIONS = [60, 120, 180, 240, 360, 480, 720, 1440]

interface CapacitySetDefiningFields {
  customerID: number | null
  containerTag: string | null
  dedicated: boolean
  portComplex: PortComplex
  bucketMinutes: number | null
}

export function useGetCapacitySetLabel() {
  const customerStore = useCustomerStore()
  onMounted(customerStore.loadIfNotLoaded)
  /** Just the scope: "Fleet" / "Acme Receiving" / "Long Haul Dedicated". */
  function getCapacitySetLabel(obj: CapacitySetDefiningFields): string {
    if (obj.containerTag) {
      return obj.containerTag + (obj.dedicated ? ' Dedicated' : '')
    }
    if (obj.customerID) {
      const customer = customerStore.getByCustomerID(obj.customerID)
      const customerName = customer ? customer.name : 'Unknown'
      return `${customerName} ${obj.dedicated ? 'Dedicated' : 'Receiving'}`
    }
    return 'Fleet'
  }
  /** Full button-style label: "Fleet Capacity (per hour)". */
  function getCapacitySetFullLabel(obj: CapacitySetDefiningFields): string {
    const base = `${getCapacitySetLabel(obj)} Capacity`
    if (obj.bucketMinutes !== null) {
      return `${base} (${formatBucketWidth(obj.bucketMinutes)})`
    }
    return base
  }
  return { getCapacitySetLabel, getCapacitySetFullLabel }
}

export function convertMinuteOfWeekToTime(
  minuteOfWeek: number,
  timeInTargetWeek: DateTime
): DateTime {
  const startOfWeek = timeInTargetWeek.startOf('week').minus({ days: 1 })
  // Adjust for short/long weeks due to daylight savings
  const endOfWeek = timeInTargetWeek.endOf('week').minus({ days: 1 })
  const hoursInWeek = Math.round(endOfWeek.diff(startOfWeek, 'hours').hours)
  const extraHoursInWeek = hoursInWeek - 168
  return startOfWeek.plus({
    minutes: minuteOfWeek,
    hours: extraHoursInWeek,
  })
}
